import React, { useState, useEffect, useCallback } from 'react';
import { ExperienceApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import LoadPanel from 'devextreme-react/load-panel';
import { handleAdminError } from '../../../common/SharedFunctions';
import List from 'devextreme-react/list';
import { loadModules, loadCss } from 'esri-loader';
import { DemoGraphicsApi } from '@apply/api';
import { ExperienceType, ExperienceProjectTypeOptions, ExperiencePropertyTypeOptions } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiamond, faCircle, faSquare } from '@fortawesome/free-solid-svg-icons';

export const PropertyMap = ({ principalId }) => {
    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);

    const [mapped, set_mapped] = useState([]);
    const [unmapped, set_unmapped] = useState([]);
    const [selected, set_selected] = useState([]);
    const [canRefresh, set_canRefresh] = useState(false);
    const [status, set_status] = useState('Loading...');

    const styles = {
        outer: { padding: '0', margin: '0', height: '100%', width: '100%' },
        selection: { float: 'left', width: '270px', padding: '10px' },
        list: {
            height: '370px',
            border: '1px solid silver',
            backgroundColor: 'white',
        },
        legend: {
            width: 'calc(100% - 270px)',
            padding: '10px',
            backgroundColor: 'white',
            border: '1px solid silver',
            float: 'left',
        },
        key: { paddingLeft: '10px', paddingRight: '50px' },
        map: {
            padding: '0',
            margin: '0',
            height: 'calc(100% - 25px)',
            width: 'calc(100% - 270px)',
            border: '1px solid silver',
            float: 'left',
        },
        listAddressIcon: {
            display: 'inline-block',
            verticalAlign: 'top',
            width: '20px',
        },
        listAddressText: {
            display: 'inline-block',
            width: 'calc(100% - 20px)',
        },
        refreshDiv: { width: '100%', marginTop: '10px', marginBottom: '10px' },
        refreshButton: { width: '100%', height: '30px' },
    };

    const loadAddresses = async (princId) => {
        if (!princId) {
            return;
        }

        try {
            set_status('Loading...');
            const response = await experienceApi.getAddresses(principalId);
            if (response.statusCode !== 200) {
                return;
            }

            const addresses = response.result;

            const mp = [];
            const ump = [];

            addresses.forEach((a) => {
                a.pointData = getPointData(a);
                if (a.canMap) {
                    mp.push(a);
                } else {
                    ump.push(a);
                }
            });

            set_mapped(mp);
            set_unmapped(ump);

            const addrsToMap = mp.length > 10 ? [...mp.slice(0, 10)] : [...mp];
            set_selected(addrsToMap);
            initializeMap(addrsToMap);
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve addresses. Please try again');
        } finally {
            set_status('');
        }
    };

    const getMiddle = (prop, markers) => {
        let values = markers.map((m) => m[prop]);
        let min = Math.min(...values);
        let max = Math.max(...values);
        if (prop === 'longitude' && max - min > 180) {
            values = values.map((val) => (val < max - 180 ? val + 360 : val));
            min = Math.min(...values);
            max = Math.max(...values);
        }
        let result = (min + max) / 2;
        if (prop === 'longitude' && result > 180) {
            result -= 360;
        }
        return result;
    };

    const findCenter = (markers) => {
        if (!markers || markers.length === 0) {
            return { longitude: -98.353, latitude: 38.449 };
        }
        return {
            latitude: getMiddle('latitude', markers),
            longitude: getMiddle('longitude', markers),
        };
    };

    const getPointData = (address) => {
        if (address.experienceTypes.includes(ExperienceType.BfiRepaid)) {
            return {
                name: 'BFI-Repaid',
                color: [255, 0, 0],
                cssColor: 'rgb(255, 0, 0)',
                shape: 'diamond',
                faIcon: faDiamond,
            };
        } else if (address.experienceTypes.includes(ExperienceType.BfiOutstanding)) {
            //Bfi Outstanding
            return {
                name: 'BFI-Outstanding',
                color: [255, 128, 0],
                cssColor: 'rgb(255, 128, 0)',
                shape: 'square',
                faIcon: faSquare,
            };
        } else {
            //Non BFI
            return {
                name: 'Non-BFI',
                color: [0, 204, 0],
                cssColor: 'rgb(0, 204, 0)',
                shape: 'circle',
                faIcon: faCircle,
            };
        }
    };

    const getProjectTypeNames = (ids) => {
        if (!ids || ids.length === 0) {
            return 'Unknown';
        }
        const options = ExperienceProjectTypeOptions.filter((a) => ids.includes(a.id));
        return options.length > 0 ? options.map((a) => a?.adminName ?? 'Unknown').join('<br/>') : 'Unknown';
    };

    const getPropertyTypeNames = (ids) => {
        if (!ids || ids.length === 0) {
            return 'Unknown';
        }
        const options = ExperiencePropertyTypeOptions.filter((a) => ids.includes(a.id));
        return options.length > 0 ? options.map((a) => a?.name ?? 'Unknown').join('<br/>') : 'Unknown';
    };

    const getAddress = (address) => {
        let line2 = '';
        if (address.street2) {
            line2 = `${address.street2}<br/>`;
        }
        return `${address.street1}<br />${line2}${address.city}, ${address.state} ${address.zipCode}`;
    };

    const getPopupContent = (address) => {
        if (!address) {
            return null;
        }

        const name = address.pointData.name;
        const projectTypeNames = getProjectTypeNames(address.projectTypeIds);
        const propertyTypeNames = getPropertyTypeNames(address.propertyTypeIds);
        const addr = getAddress(address);
        return `<table><tbody><tr><td style='width:140px;'>Experience. Type:</td><td><b>${name}</b></td></tr><tr><td>Project Type(s):</td><td><b>${projectTypeNames}</b></td></tr><tr><td>Structure Type(s):</td><td><b>${propertyTypeNames}</b></td></tr><tr><td>Address:</td><td><b>${addr}</b></td></tr></table>`;
    };

    const initializeMap = useCallback(
        async (addresses) => {
            let view;

            const modules = [
                'esri/Map',
                'esri/views/MapView',
                'esri/config',
                'esri/Graphic',
                'esri/layers/GraphicsLayer',
                'esri/core/reactiveUtils',
            ];
            const [Map, MapView, esriConfig, Graphic, GraphicsLayer, reactiveUtils] = await loadModules(modules);

            const addAddressToGraphicsLayer = (graphicsLayer, address) => {
                const latitude = address.latitude;
                const longitude = address.longitude;
                if (!latitude || !longitude) {
                    return;
                }

                // Create a point
                var point = {
                    type: 'point',
                    longitude,
                    latitude,
                };

                var simpleMarkerSymbol = {
                    type: 'simple-marker', //
                    color: address.pointData.color,
                    size: 8,
                    style: address.pointData.shape,
                    outline: {
                        color: address.pointData.color, // white
                        width: 1,
                    },
                };

                // Create attributes
                var attributes = {
                    Name: `${address.street1}`,
                    Location: getAddress(address),
                };

                // Create popup template
                var popupTemplate = {
                    location: point,
                    title: `${address.street1}`,
                    content: getPopupContent(address),
                };

                var pointGraphic = new Graphic({
                    geometry: point,
                    symbol: simpleMarkerSymbol,
                    attributes: attributes,
                    popupTemplate: popupTemplate,
                });

                graphicsLayer.add(pointGraphic);
            };

            try {
                set_status('Loading map...');
                loadCss();
                const map = new Map({ basemap: 'streets-navigation-vector' });
                esriConfig.apiKey = DemoGraphicsApi.apiKey;

                const midPoint = findCenter(addresses);

                view = new MapView({
                    map: map,
                    zoom: 4, //zoomLevel,
                    container: 'mapDiv',
                    center: [midPoint.longitude, midPoint.latitude],
                    ui: {
                        components: ['attribution', 'zoom'],
                    },
                });

                var graphicsLayer = new GraphicsLayer();
                map.add(graphicsLayer);

                for (let i = 0; i < addresses.length; i++) {
                    addAddressToGraphicsLayer(graphicsLayer, addresses[i]);
                }

                //const fs = new FeatureSet(ArrayCollection(graphicsLayer.graphicProvider).toArray())
                view.goTo(graphicsLayer.graphics.items, {
                    animate: false,
                }).then((response) => {
                    var zoomView = {};
                    zoomView = view.extent.expand(0.95);
                    view.goTo(zoomView);
                });

                //Commented out on purpose!
                //Show Details on hover
                // view.on('pointer-move', function (event) {
                //   view.hitTest(event).then(function (response) {
                //     if (response.results.length) {
                //       var graphic = response.results.filter(function (result) {
                //         // check if the graphic belongs to the layer of interest
                //         return result.graphic.layer === graphicsLayer;
                //       })[0]?.graphic;
                //       if (graphic) {
                //         view.popup.open({
                //           location: graphic.geometry.centroid,
                //           features: [graphic],
                //         });
                //       } else {
                //         view.popup.close();
                //       }
                //     } else {
                //       view.popup.close();
                //     }
                //   });
                // });

                reactiveUtils.watch(
                    () => !view?.updating,
                    () => {
                        set_status('');
                    },
                    { once: false }
                );
                set_canRefresh(false);
            } catch (ex) {
                set_status('');
            }
            return view;
        },
        [selected]
    );

    useEffect(() => {
        if (!principalId) {
            return;
        }

        loadAddresses(principalId);
    }, [principalId]);

    const refreshMap = async () => {
        const renderMapAsync = async (addresses) => {
            const view = initializeMap(addresses);
            return () => view?.destroy();
        };
        await renderMapAsync(selected);
    };

    const addressListOptionRender = (address) => {
        if (!address) {
            return null;
        }

        const line1 = address.street1;
        const line2 = address.street2;
        const line3 = `${address.city}, ${address.state} ${address.zipCode}`;

        return (
            <span>
                <span style={styles.listAddressIcon}>
                    <FontAwesomeIcon
                        icon={address.pointData.faIcon}
                        aria-hidden='true'
                        style={{ color: address.pointData.cssColor }}
                    />
                </span>
                <span style={styles.listAddressText}>
                    {line1}
                    {line2 && <br />} {line2}
                    <br />
                    {line3}
                </span>
            </span>
        );
    };

    const onOptionChanged = (e) => {
        if (e.name === 'selectedItemKeys') {
            set_selected(e.value);
            set_canRefresh(true);
        }
    };

    if (!principalId) {
        return null;
    }

    return (
        <div style={styles.outer}>
            <div style={styles.selection}>
                <h2 className='decorated'>Geocoded Addresses</h2>
                <div style={styles.list}>
                    <List
                        id='mapped'
                        items={mapped}
                        valueExpr='id'
                        displayExpr={'id'}
                        showSelectionControls={true}
                        placeholder='Select properties to include in the Loan'
                        selectionMode='all'
                        itemRender={addressListOptionRender}
                        selectedItems={selected}
                        onOptionChanged={onOptionChanged}
                        noDataText='No mapped addresses'
                        useNativeScrolling={true}
                    />
                </div>
                <div style={styles.refreshDiv}>
                    <button
                        style={styles.refreshButton}
                        onClick={refreshMap}
                        className='button primary'
                        disabled={!canRefresh}
                        title='Click to rerender the map with the checked addresses'
                    >
                        Update Map
                    </button>
                </div>
                <h2 className='decorated'>Ungeocoded Addresses</h2>
                <div style={styles.list}>
                    <List
                        id='unmapped'
                        items={unmapped}
                        valueExpr='id'
                        displayExpr={'id'}
                        showSelectionControls={false}
                        placeholder='Properties that have not been geocoded'
                        selectionMode='none'
                        itemRender={addressListOptionRender}
                        noDataText='No unmapped addresses'
                        useNativeScrolling={true}
                    />
                </div>
            </div>
            <div style={styles.legend}>
                <span style={styles.key}>
                    <FontAwesomeIcon
                        icon={faCircle}
                        aria-hidden='true'
                        style={{ color: 'rgb(0, 204, 0)' }}
                    />
                    &nbsp;Non-Bfi
                </span>
                <span style={styles.key}>
                    <FontAwesomeIcon
                        icon={faDiamond}
                        aria-hidden='true'
                        style={{ color: 'rgb(255, 0, 0])' }}
                    />
                    &nbsp;BFI-Repaid
                </span>
                <span style={styles.key}>
                    <FontAwesomeIcon
                        icon={faSquare}
                        aria-hidden='true'
                        style={{ color: 'rgb(255, 128, 0)' }}
                    />
                    &nbsp;BFI-Outstanding
                </span>
            </div>
            <div
                id='mapDiv'
                style={styles.map}
            ></div>

            <LoadPanel
                message={status}
                hideOnOutsideClick={false}
                visible={!!status}
            />
        </div>
    );
};
